/*
 * "Add free gift" button of the cart gifts strip (snippets/ts-cart-gifts.liquid).
 *
 * Adds the gift variant to the cart, then tells Horizon about it the same way its own add-to-cart does
 * (a CartLinesUpdateEvent with the new cart and the re-rendered cart sections), so the cart page, the cart drawer and the
 * header bag count all refresh by themselves.
 */
(function () {
  if (customElements.get('ts-cart-gifts')) return;

  class TsCartGifts extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this._onClick);
      this.removeStale();
      // Horizon refreshes the cart by morphing the existing strip, so this element is not connected again when a gift turns into "keep":
      // watch for the marker to appear inside it.
      this._observer = new MutationObserver(() => this.removeStale());
      this._observer.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this._observer) this._observer.disconnect();
    }

    _onClick = (event) => {
      var button = event.target.closest('[data-ts-gift-add]');
      if (!button || button.disabled) return;
      this.addGift(button);
    };

    // Gifts are $0 products, so a gift whose cart no longer qualifies has to be taken out here (the strip marks it with data-ts-gift-remove).
    async removeStale() {
      var ids = [];
      this.querySelectorAll('[data-ts-gift-remove]').forEach(function (marker) {
        var id = marker.dataset.variantId;
        if (id && ids.indexOf(id) === -1 && (TsCartGifts.failed[id] || 0) < 3) ids.push(id);
      });
      if (!ids.length || TsCartGifts.busy) return;
      TsCartGifts.busy = true;
      try {
        var events = await import('@shopify/events');
        var CartLinesUpdateEvent = events.CartLinesUpdateEvent;
        var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        var lines = (cart.items || []).filter(function (i) { return ids.indexOf(String(i.variant_id)) > -1; });
        if (!lines.length) return;
        var deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: lines.map(function (l) { return { id: l.key, quantity: 0 }; }),
            promise: deferred.promise,
          })
        );
        var sectionIds = Array.prototype.slice
          .call(document.querySelectorAll('cart-items-component'))
          .map(function (el) { return el.dataset.sectionId; })
          .filter(Boolean);
        var last = null;
        try {
          for (var n = 0; n < lines.length; n++) {
            var response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: lines[n].key, quantity: 0, sections: sectionIds.join(','), sections_url: window.location.pathname }),
            });
            last = await response.json();
            if (!response.ok || last.status || last.errors) throw new Error('Could not remove the gift');
          }
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(last),
            detail: { items: last.items, source: 'ts-cart-gifts', sourceId: 'ts-cart-gifts', itemCount: 0, sections: last.sections, didError: false },
          });
        } catch (err) {
          ids.forEach(function (id) { TsCartGifts.failed[id] = (TsCartGifts.failed[id] || 0) + 1; });
          deferred.reject(err);
        }
      } catch (err) {
        ids.forEach(function (id) { TsCartGifts.failed[id] = (TsCartGifts.failed[id] || 0) + 1; });
      } finally {
        TsCartGifts.busy = false;
      }
    }

    async addGift(button) {
      var variantId = button.dataset.variantId;
      var error = this.querySelector('.ts-cart-gifts__error');
      if (error) error.hidden = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      var events = await import('@shopify/events');
      var CartLinesUpdateEvent = events.CartLinesUpdateEvent;
      var deferred = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );

      var sectionIds = Array.prototype.slice
        .call(document.querySelectorAll('cart-items-component'))
        .map(function (el) { return el.dataset.sectionId; })
        .filter(Boolean);

      try {
        var response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        var data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add the gift');
        }

        var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'ts-cart-gifts',
            sourceId: 'ts-cart-gifts',
            itemCount: 1,
            productId: button.dataset.productId,
            sections: data.sections,
            didError: false,
          },
        });
      } catch (err) {
        deferred.reject(err);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        }
      }
    }
  }

  TsCartGifts.busy = false;
  TsCartGifts.failed = {};

  customElements.define('ts-cart-gifts', TsCartGifts);
})();
